import { Master } from 'little-state-machine';
import React, { createContext, PropsWithChildren, useContext } from 'react';

import { useGlobalStateMachine } from '../GlobalStore';
import { MasterKey } from '../models/gen/masterKey';

export type MasterContext = {
  applyUnit: Partial<
    Record<
      | 'shiftApply'
      | 'attendance1'
      | 'attendance2'
      | 'payslip'
      | 'expense1'
      | 'expense2'
      | 'invoice',
      Master
    >
  >;
  // attendanceType: Partial<
  //   Record<'absent' | 'holiday' | 'leave' | 'present' | 'suspension', Master>
  // >;
  attendanceType: Record<number, Master>;
  expenseItem: Record<number, Master>;
  extraFee: Record<number, Master>;
  noticeCategory: Record<number, Master>;
  role: Partial<
    Record<
      'se' | 'exam' | 'tech' | 'shift' | 'representative' | 'member',
      Master
    >
  >;
  task: Record<number, Master>;
  getBy: (key: MasterKey, id: number) => Master | undefined;
};

const contextValue: MasterContext = {
  applyUnit: {},
  attendanceType: {},
  expenseItem: {},
  extraFee: {},
  noticeCategory: {},
  role: {},
  task: {},
  getBy(key, id) {
    return undefined;
  },
};
const masterContext = createContext<MasterContext>(contextValue);

export const useMasterContext = () => useContext(masterContext);

export const MasterProvider: React.FunctionComponent<PropsWithChildren> = (
  props
) => {
  const { state } = useGlobalStateMachine();
  const master = state.master || {};

  const shiftApply = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find((record) => record.applyType === 1) as
        | Master
        | undefined,
    [master[MasterKey.ApplyUnits]]
  );
  const attendance1 = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find(
        (record) => record.applyType === 2 && record.name === '前半'
      ) as Master | undefined,
    [master[MasterKey.ApplyUnits]]
  );
  const attendance2 = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find(
        (record) => record.applyType === 2 && record.name === '後半'
      ) as Master | undefined,
    [master[MasterKey.ApplyUnits]]
  );
  const payslip = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find((record) => record.applyType === 3) as
        | Master
        | undefined,
    [master[MasterKey.ApplyUnits]]
  );
  const expense1 = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find(
        (record) => record.applyType === 4 && record.name === '前半'
      ) as Master | undefined,
    [master[MasterKey.ApplyUnits]]
  );
  const expense2 = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find(
        (record) => record.applyType === 4 && record.name === '後半'
      ) as Master | undefined,
    [master[MasterKey.ApplyUnits]]
  );
  const invoice = React.useMemo(
    () =>
      master[MasterKey.ApplyUnits]?.find((record) => record.applyType === 5) as
        | Master
        | undefined,
    [master[MasterKey.ApplyUnits]]
  );

  // const present = React.useMemo(
  //   () =>
  //     master[MasterKey.AttendanceTypes]?.find(
  //       (record) =>
  //         record.id ===
  //         state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WORK
  //     ) as Master | undefined,
  //   [master[MasterKey.AttendanceTypes]]
  // );
  // const holiday = React.useMemo(
  //   () =>
  //     master[MasterKey.AttendanceTypes]?.find(
  //       (record) =>
  //         record.id ===
  //         state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_HOLIDAY
  //     ) as Master | undefined,
  //   [master[MasterKey.AttendanceTypes]]
  // );
  // const absent = React.useMemo(
  //   () =>
  //     master[MasterKey.AttendanceTypes]?.find(
  //       (record) =>
  //         record.id ===
  //         state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_ABSENCE
  //     ) as Master | undefined,
  //   [master[MasterKey.AttendanceTypes]]
  // );
  // const leave = React.useMemo(
  //   () =>
  //     master[MasterKey.AttendanceTypes]?.find(
  //       (record) =>
  //         record.id ===
  //         state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WITH_PAY
  //     ) as Master | undefined,
  //   [master[MasterKey.AttendanceTypes]]
  // );
  // const suspension = React.useMemo(
  //   () =>
  //     master[MasterKey.AttendanceTypes]?.find(
  //       (record) =>
  //         record.id ===
  //         state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_SUSPENSION
  //     ) as Master | undefined,
  //   [master[MasterKey.AttendanceTypes]]
  // );

  const se = React.useMemo(
    () =>
      master[MasterKey.Roles]?.find((record) => record.id === 1) as
        | Master
        | undefined,
    [master[MasterKey.Roles]]
  );
  const exam = React.useMemo(
    () =>
      master[MasterKey.Roles]?.find((record) => record.id === 2) as
        | Master
        | undefined,
    [master[MasterKey.Roles]]
  );
  const tech = React.useMemo(
    () =>
      master[MasterKey.Roles]?.find((record) => record.id === 3) as
        | Master
        | undefined,
    [master[MasterKey.Roles]]
  );
  const shift = React.useMemo(
    () =>
      master[MasterKey.Roles]?.find((record) => record.id === 4) as
        | Master
        | undefined,
    [master[MasterKey.Roles]]
  );
  const representative = React.useMemo(
    () =>
      master[MasterKey.Roles]?.find((record) => record.id === 5) as
        | Master
        | undefined,
    [master[MasterKey.Roles]]
  );
  const member = React.useMemo(
    () =>
      master[MasterKey.Roles]?.find((record) => record.id === 6) as
        | Master
        | undefined,
    [master[MasterKey.Roles]]
  );

  const notification = React.useMemo(
    () =>
      master[MasterKey.NoticeCategories]?.find((record) =>
        record.name?.startsWith('お知らせ')
      ),
    [master[MasterKey.NoticeCategories]]
  );
  const announcement = React.useMemo(
    () =>
      master[MasterKey.NoticeCategories]?.find((record) =>
        record.name?.startsWith('通達')
      ),
    [master[MasterKey.NoticeCategories]]
  );

  const values: MasterContext = React.useMemo(
    () => ({
      applyUnit: {
        shiftApply,
        attendance1,
        attendance2,
        payslip,
        expense1,
        expense2,
        invoice,
      },
      // attendanceType: { absent, holiday, leave, present, suspension },
      attendanceType: (master[MasterKey.AttendanceTypes] || []).reduce(
        (prev, cur) => ({ ...prev, [cur.id]: cur }),
        {}
      ),
      expenseItem: (master[MasterKey.ExpenseItems] || []).reduce(
        (prev, cur) => ({ ...prev, [cur.id]: cur }),
        {}
      ),
      // 多いから
      extraFee: (master[MasterKey.ExtraFees] || []).reduce(
        (prev, cur) => ({ ...prev, [cur.id]: cur }),
        {}
      ),
      noticeCategory: { announcement, notification },
      role: { exam, member, representative, se, shift, tech },
      // 多いから
      task: (master[MasterKey.Tasks] || []).reduce(
        (prev, cur) => ({ ...prev, [cur.id]: cur }),
        {}
      ),
      getBy: (key, id) => master[key].find((record) => record.id === id),
    }),
    [master]
  );

  return (
    <masterContext.Provider value={values}>
      {props.children}
    </masterContext.Provider>
  );
};
